/**
 * Structured Data helpers (JSON-LD).
 * Return plain objects to be passed into SEOHead's `jsonLd` prop.
 */

export function orgJsonLd(branding) {
  if (!branding) return null;
  const sameAs = [];
  if (branding.facebook) sameAs.push(`https://facebook.com/${branding.facebook.replace(/^@/, "")}`);
  if (branding.instagram) sameAs.push(`https://instagram.com/${branding.instagram.replace(/^@/, "")}`);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: branding.name,
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: _abs(branding.logo),
    ...(branding.email ? { email: branding.email } : {}),
    ...(branding.whatsapp ? { telephone: `+${branding.whatsapp.replace(/^\+?/, "")}` } : {}),
    ...(branding.address ? { address: { "@type": "PostalAddress", streetAddress: branding.address } } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  };
}

export function websiteJsonLd(branding) {
  if (!branding) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: branding.name,
    url: origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${origin}/katalog?search={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function localBusinessJsonLd(branding, localCfg) {
  if (!branding || !localCfg?.enabled) return null;
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: branding.name,
    image: _abs(branding.logo),
    ...(branding.email ? { email: branding.email } : {}),
    ...(branding.whatsapp ? { telephone: `+${branding.whatsapp.replace(/^\+?/, "")}` } : {}),
    ...(branding.address ? { address: { "@type": "PostalAddress", streetAddress: branding.address } } : {}),
    ...(branding.maps_url ? { hasMap: branding.maps_url } : {}),
    priceRange: localCfg.price_range || "$$",
    openingHours: localCfg.opening_hours || "Mo-Su 08:00-20:00",
  };
}

export function productJsonLd(p, branding) {
  if (!p) return null;
  const price = p.sale_price || p.price;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    image: _abs(p.image),
    description: p.description || "",
    sku: p.sku,
    brand: { "@type": "Brand", name: p.brand || branding?.name || "" },
    ...(p.category ? { category: p.category } : {}),
    offers: {
      "@type": "Offer",
      priceCurrency: "IDR",
      price: price,
      availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: typeof window !== "undefined" ? window.location.href : "",
    },
  };
}

export function breadcrumbJsonLd(items) {
  if (!items?.length) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url?.startsWith("http") ? it.url : `${origin}${it.url || ""}`,
    })),
  };
}

function _abs(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (typeof window === "undefined") return u;
  return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
}
